import React, { memo, useRef, useState } from 'react';
import { Button, Input, InputRef, Rate, Space, Table } from 'antd';
import { ColumnType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { Article, NoContentArticle, Status, StatusValue } from '../admin-knowledge-base.type';
import { getStatusValue } from '../admin-knowledge-base.utils';
import { displayedStatusMap, statusColorsMap } from '../admin-knowledge-base.constants';
import { DeleteOutlined, EditOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { FilterConfirmProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { NO_DATA } from '/react4xp/parts/knowledge-base-common/knowledge-base-common.constants';
import { validatePrivileges } from 'mixins/helpers';

export interface ArticlesTableProps {
    articles: NoContentArticle[];
    showUpdateForm: (article: Article) => void;
    showDeleteForm: (article: Article) => void;
    setActive: (article: Article) => void;
    roles: string[];
    privileges: string[];
}

const ArticlesTable = memo(
    ({
        articles,
        showUpdateForm,
        showDeleteForm,
        setActive,
        roles,
        privileges,
    }: ArticlesTableProps) => {
        const [searchText, setSearchText] = useState('');
        const searchInput = useRef<InputRef>(null);

        const authors = [...new Set(articles.map((article) => article.createdBy))].filter(Boolean);
        const editors = [...new Set(articles.map((article) => article.updatedBy))].filter(Boolean);

        const handleSearch = (
            selectedKeys: string[],
            confirm: (param?: FilterConfirmProps) => void
        ) => {
            setSearchText(selectedKeys[0]);
            confirm();
        };

        const handleReset = (clearFilters: () => void, confirm) => {
            setSearchText('');
            clearFilters();
            handleSearch([], confirm);
        };

        const getColumnSearchProps = (): ColumnType<Article> => ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div
                    style={{ padding: 8 }}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <Input
                        ref={searchInput}
                        placeholder={`Искать в заголовках`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys as string[], confirm)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Поиск
                        </Button>
                        <Button
                            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Сброс
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                close();
                            }}
                        >
                            Отмена
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
            ),
            onFilter: (value, record) =>
                record.ru.title
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase()),
            onFilterDropdownOpenChange: (visible) => {
                if (visible) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
            render: (_, record: Article) => (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={record.ru.title ? record.ru.title.toString() : ''}
                />
            ),
        });

        const columns: ColumnType<Article>[] = [
            {
                title: 'Наименование',
                key: 'title',
                render: (_, record: Article) => record.ru.title,
                // fixed: 'left',
                width: '300px',
                sorter: (a, b) => a.ru.title.localeCompare(b.ru.title),
                sortDirections: ['ascend', 'descend'],
                ...getColumnSearchProps(),
            },
            {
                title: 'Статус',
                dataIndex: 'status',
                key: 'status',
                render: (value: Status) => {
                    const statusValue = getStatusValue(value);
                    return (
                        <span style={{ color: statusColorsMap[statusValue] }}>
                            {displayedStatusMap[statusValue]}
                        </span>
                    );
                },
                filters: Object.keys(displayedStatusMap)
                    .filter((key) => key !== StatusValue.DELETED)
                    .map((key) => ({
                        text: displayedStatusMap[key],
                        value: key,
                    })),
                onFilter: (value: string, record) => getStatusValue(record.status) === value,
                sorter: (a, b) => {
                    const displayed = [a, b].map(
                        ({ status }) => displayedStatusMap[getStatusValue(status)]
                    );
                    return displayed[0].localeCompare(displayed[1]);
                },
                sortDirections: ['ascend', 'descend'],
            },
            {
                title: 'Дата создания',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (value) => dayjs(value).format('DD.MM.YYYY hh:mm'),
                sorter: (a, b) => {
                    const dates = [a, b].map(({ createdAt }) => Number(new Date(createdAt)));
                    return dates[0] - dates[1];
                },
                sortDirections: ['ascend', 'descend'],
            },
            {
                title: 'Автор',
                dataIndex: 'createdBy',
                key: 'createdBy',
                sorter: (a, b) => a.createdBy?.localeCompare(b.createdBy),
                sortDirections: ['ascend', 'descend'],
                filters: authors.map((author) => ({
                    text: author,
                    value: author,
                })),
                onFilter: (value: string, record) => record.createdBy === value,
            },
            {
                title: 'Дата изменения',
                dataIndex: 'updatedAt',
                key: 'updatedAt',
                render: (value) => (value ? dayjs(value).format('DD.MM.YYYY hh:mm') : NO_DATA),
                sorter: (a, b) => {
                    const dates = [a, b].map(({ updatedAt }) => Number(new Date(updatedAt ?? 0)));
                    return dates[0] - dates[1];
                },
                sortDirections: ['ascend', 'descend'],
            },
            {
                title: 'Редактор',
                dataIndex: 'updatedBy',
                key: 'updatedBy',
                render: (value) => (value ? value : NO_DATA),
                sorter: (a, b) => a.updatedBy?.localeCompare(b.updatedBy),
                sortDirections: ['ascend', 'descend'],
                filters: editors.map((editor) => ({
                    text: editor,
                    value: editor,
                })),
                onFilter: (value: string, record) => record.updatedBy === value,
            },
            {
                title: 'Оценка',
                dataIndex: 'rating',
                key: 'rating',
                render: (rating) => (
                    <Space style={{ transform: 'scale(0.6)', margin: -30 }}>
                        <Rate
                            allowHalf
                            defaultValue={rating ?? NO_DATA}
                            disabled
                        />
                    </Space>
                ),
                sorter: (a, b) => a.rating - b.rating,
                sortDirections: ['ascend', 'descend'],
            },
            {
                title: 'Действия',
                key: 'actions',
                render: (_, record: Article) => (
                    <Space>
                        {validatePrivileges(roles, privileges, 'PRIV_ARTICLE_VIEW') && (
                            <Button
                                type="ghost"
                                onClick={() => setActive(record)}
                                icon={<EyeOutlined />}
                            />
                        )}
                        {validatePrivileges(roles, privileges, 'PRIV_ARTICLE_EDIT') && (
                            <Button
                                type="ghost"
                                onClick={() => showUpdateForm(record)}
                                icon={<EditOutlined />}
                            />
                        )}
                        {validatePrivileges(roles, privileges, 'PRIV_ARTICLE_DELETE') && (
                            <Button
                                type="ghost"
                                onClick={() => showDeleteForm(record)}
                                icon={<DeleteOutlined />}
                            />
                        )}
                    </Space>
                ),
            },
        ];

        return (
            <Table
                columns={columns}
                rowKey="id"
                dataSource={articles}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
            />
        );
    }
);

export default ArticlesTable;
